import { useEffect, useRef } from 'react';

import {
  IntegrationAccountRemovedPayload,
  MessagingPlatform,
  WebSocketEntityType,
} from '@this+that/models';
import log from 'loglevel';

import { useGetIntegrations } from '../../../hooks';
import {
  useCommunicationIntentsStore,
  useFetchCacheStore,
  useIntegrationsStore,
  useLabelsStore,
  useTeamsCommunicationsStore,
  useUserCommunicationsStore,
} from '../../../store';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const EMAIL_ACCOUNT_PLATFORMS = [
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
  MessagingPlatform.GOOGLE_CHAT,
];

const LABEL_OWNING_PLATFORMS = new Set([
  MessagingPlatform.GMAIL,
  MessagingPlatform.OUTLOOK,
]);

export function useIntegrationAccountsSync(): void {
  const { subscribe } = useWebSocket();
  const { handleGetIntegrations } = useGetIntegrations();
  // useGetIntegrations returns a new handleGetIntegrations closure every
  // render, and it subscribes to the whole integrations store with no
  // selector — depending on it directly would unsubscribe/resubscribe this
  // effect on every integrations change, including the ones this handler's
  // own handleGetIntegrations(true) call causes.
  const handleGetIntegrationsRef = useRef(handleGetIntegrations);

  useEffect(() => {
    handleGetIntegrationsRef.current = handleGetIntegrations;
  }, [handleGetIntegrations]);

  useEffect(() => {
    const unsubscribe = subscribe<IntegrationAccountRemovedPayload>(
      WebSocketEntityType.IntegrationAccount,
      WebSocketEventType.REMOVE,
      async ({ item }) => {
        if (!item) {
          return;
        }

        const { platform, platformAccountId } = item;

        if (EMAIL_ACCOUNT_PLATFORMS.includes(platform) && platformAccountId) {
          const ids = useUserCommunicationsStore
            .getState()
            .getMessageIdsByEmail(platformAccountId, platform);

          useCommunicationIntentsStore.getState().clearIntents(ids);

          // Same GOOGLE_CHAT carve-out as the labels clear below: a Chat
          // disconnect must not blocklist the Gmail address it reuses.
          if (LABEL_OWNING_PLATFORMS.has(platform)) {
            useIntegrationsStore
              .getState()
              .markEmailAccountRemoved(platform, platformAccountId);
          }

          useUserCommunicationsStore
            .getState()
            .clearUserCommunicationsByEmail(platformAccountId, platform);

          // Labels belong to the mail surface only — a Google Chat disconnect
          // (which the backend publishes with platformAccountId set to the
          // Gmail address) must not clear Gmail's labels.
          if (LABEL_OWNING_PLATFORMS.has(platform)) {
            useLabelsStore
              .getState()
              .clearLabelsByEmailAddress(platformAccountId);
          }

          useFetchCacheStore.getState().invalidateAllViewTypes();

          await handleGetIntegrationsRef.current(true);
          return;
        }

        if (platform === MessagingPlatform.TEAMS) {
          if (platformAccountId) {
            useTeamsCommunicationsStore
              .getState()
              .clearTeamsChatsByHomeAccountId(platformAccountId);
            // Safety net for untagged chats an older build persisted.
            useFetchCacheStore.getState().invalidateTeamsChats();
          } else {
            useTeamsCommunicationsStore.getState().resetState();
            useFetchCacheStore.getState().invalidateTeamsChats();
          }
          await handleGetIntegrationsRef.current(true);
          return;
        }

        log.debug(
          '[IntegrationAccountsSync] Ignoring REMOVE for unhandled platform',
          platform,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [subscribe]);
}
